import { create } from 'zustand';

import { ApiError, api } from '../lib/api.js';
import { isEmbedded, postToHost } from '../lib/embed.js';
import { useLayout } from './layout.js';

/**
 * Open-editor state: which project is active, which files are open, and where
 * the caret is. Buffers live here so switching tabs never refetches.
 */

export interface OpenFile {
  readonly path: string;
  readonly language: string;
  /** Current in-editor content, which may differ from disk. */
  content: string;
  /** Content as last read from or written to disk, for dirty checking. */
  savedContent: string;
  modifiedAt: string | null;
}

/** Top-level editor tabs. */
export type WorkspaceView = 'scene' | 'model' | 'pixel' | 'sound' | 'video' | 'play' | 'code' | 'board' | 'team' | 'settings';

/** What the Code tab's sidebar shows. */
export type CodeSide = 'explorer' | 'git';

/** Editor views (as opposed to Board / Team / Settings pages). */
export const DOCKED_AI_VIEWS: readonly WorkspaceView[] = ['scene', 'model', 'pixel', 'sound', 'video', 'play', 'code'];

interface WorkspaceState {
  projectId: number | null;
  projectName: string;
  files: Record<string, OpenFile>;
  /** Tab order; the last entry is the active tab. */
  tabs: string[];
  view: WorkspaceView;
  codeSide: CodeSide;
  /** The AI panel is somewhere in the dock layout (Ctrl+J). Mirrors state/layout. */
  aiOpen: boolean;

  openProject: (projectId: number, name: string) => Promise<void>;
  closeProject: () => void;
  openFile: (path: string) => Promise<void>;
  saveFile: (path: string) => Promise<boolean>;
  closeTab: (path: string) => void;
  setContent: (path: string, content: string) => void;
  setView: (view: WorkspaceView) => void;
  setCodeSide: (side: CodeSide) => void;
  setAiOpen: (open: boolean) => void;
  toggleAi: () => void;
  /** Re-read an open file after it changed on disk, unless it has unsaved edits. */
  reloadFromDisk: (path: string) => Promise<void>;
  activePath: () => string | null;
  isDirty: (path: string) => boolean;
  anyDirty: () => boolean;
}

export const useWorkspace = create<WorkspaceState>((set, get) => ({
  projectId: null,
  projectName: '',
  files: {},
  tabs: [],
  view: 'scene',
  codeSide: 'explorer',
  aiOpen: useLayout.getState().isOpen('ai'),

  openProject: async (projectId, name) => {
    // Switching project discards unsaved buffers rather than silently saving
    // them into a different project's folder.
    set({ projectId, projectName: name, files: {}, tabs: [] });
  },

  closeProject: () => {
    // A project tab's frame can't close itself: the host flushes saves,
    // confirms unsaved work and removes the tab.
    if (isEmbedded()) {
      postToHost({ type: 'close' });
      return;
    }
    set({ projectId: null, projectName: '', files: {}, tabs: [] });
  },

  openFile: async (path) => {
    const { projectId, files } = get();
    if (!projectId) return;

    // Already open: just focus it.
    if (files[path]) {
      set((state) => ({
        tabs: [...state.tabs.filter((t) => t !== path), path],
      }));
      return;
    }

    try {
      const result = await api.get<{
        file: { path: string; content: string; modifiedAt: string };
      }>(`/projects/${projectId}/file?path=${encodeURIComponent(path)}`);

      const file: OpenFile = {
        path: result.file.path,
        language: 'plaintext',
        content: result.file.content,
        savedContent: result.file.content,
        modifiedAt: result.file.modifiedAt,
      };
      set((state) => ({
        files: { ...state.files, [file.path]: file },
        tabs: [...state.tabs.filter((t) => t !== file.path), file.path],
      }));
    } catch (error) {
      // Surfaced by the caller; nothing to do but leave the tab closed.
      void error;
    }
  },

  saveFile: async (path) => {
    const { projectId, files } = get();
    const file = files[path];
    if (!projectId || !file) return false;

    try {
      const result = await api.put<{ file: { path: string; modifiedAt: string } }>(
        `/projects/${projectId}/file`,
        { path, content: file.content },
      );
      set((state) => {
        const current = state.files[path];
        if (!current) return state;
        return {
          files: {
            ...state.files,
            [path]: {
              ...current,
              savedContent: current.content,
              modifiedAt: result.file.modifiedAt,
            },
          },
        };
      });
      return true;
    } catch (error) {
      void (error instanceof ApiError ? error.message : error);
      return false;
    }
  },

  closeTab: (path) => {
    set((state) => {
      const files = { ...state.files };
      delete files[path];
      const tabs = state.tabs.filter((t) => t !== path);
      return { files, tabs };
    });
  },

  setContent: (path, content) => {
    set((state) => {
      const file = state.files[path];
      if (!file) return state;
      return { files: { ...state.files, [path]: { ...file, content } } };
    });
  },

  setView: (view) => set({ view }),
  setCodeSide: (codeSide) => set({ codeSide }),
  setAiOpen: (open) => {
    const layout = useLayout.getState();
    if (open) layout.show('ai');
    else if (layout.isOpen('ai')) layout.toggle('ai');
  },
  toggleAi: () => useLayout.getState().toggle('ai'),

  reloadFromDisk: async (path) => {
    const { projectId, files } = get();
    const open = files[path];
    if (!projectId || !open || open.content !== open.savedContent) return;
    try {
      const result = await api.get<{ file: { path: string; content: string; modifiedAt: string } }>(
        `/projects/${projectId}/file?path=${encodeURIComponent(path)}`,
      );
      set((state) => {
        const current = state.files[path];
        // Skip if the user started typing while the read was in flight.
        if (!current || current.content !== current.savedContent) return state;
        if (current.content === result.file.content) return state;
        return {
          files: {
            ...state.files,
            [path]: {
              ...current,
              content: result.file.content,
              savedContent: result.file.content,
              modifiedAt: result.file.modifiedAt,
            },
          },
        };
      });
    } catch {
      // Deleted or unreadable: leave the buffer as it is.
    }
  },

  activePath: () => {
    const { tabs } = get();
    return tabs.length > 0 ? tabs[tabs.length - 1]! : null;
  },

  isDirty: (path) => {
    const file = get().files[path];
    return file ? file.content !== file.savedContent : false;
  },

  anyDirty: () => {
    const { files } = get();
    return Object.values(files).some((f) => f.content !== f.savedContent);
  },
}));

// The AI panel's presence is owned by the dock layout.
useLayout.subscribe((layout) => {
  const open = layout.isOpen('ai');
  if (open !== useWorkspace.getState().aiOpen) useWorkspace.setState({ aiOpen: open });
});
